<template>
  <div>
    <div v-if="showReportMessage" class="dashboard-row big message">
      <InsightsTableTitle
        :tooltip="tooltipMessage"
        :title="shopperIntelCard.title"
        :subheading="shopperIntelCard.tooltip"
      />
      <ChartBlock>
        <ReportMessage
          :message="statSigMessage"
          class="insights-report-message"
        />
      </ChartBlock>
    </div>
    <div v-else>
      <div>
        <TableTile
          class="shopper-intel-tile"
          :data="formattedTableData"
          :dimensions="['allSegments']"
          :error="error"
          :loading="loading"
          :metrics="['measurableViews', 'purchaseIntentRate']"
          :title="shopperIntelCard.title"
          :tooltip="shopperIntelCard.tooltip"
          @iconClick="emitIconClick(shopperIntelCard.title)"
        />
      </div>
      <div class="dashboard-row-demographics">
        <DemographicsPieChartCard
          class="gender-chart"
          :data="formattedPieChartData"
          :loading="loading"
          :error="error"
          :showThresholdMessage="showPieChartThresholdMessage"
          :isExternalData="true"
          @iconClick="emitIconClick"
        />
        <DemographicsBarChartCard
          :data="formattedBarChartData"
          :loading="loading"
          :error="error"
          :showThresholdMessage="showBarChartThresholdMessage"
          :isExternalData="true"
          dimensionKey="Demographics_____Age"
          primaryKey="pct_of_page_views"
          @iconClick="emitIconClick"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { insightsCardsMap } from '../../dashboard/Views/config/insightsCards';
import DemographicsBarChartCard from
  '../Demographics/DemographicsCard/DemographicsBarChartCard.vue';
import DemographicsPieChartCard from
  '../Demographics/DemographicsCard/DemographicsPieChartCard.vue';
import InsightsTableTitle from '../Insights/Tables/InsightsTableTitle.vue';
import ChartBlock from '../Common/Charting/Shared/ChartBlock.vue';
import ReportMessage from '../Common/ReportMessage.vue';
import TableTile from '../Common/TableTile.vue';
import {
  getReportFieldFromValue,
} from '../../../services/dashboard/reportFieldService';

export default {
  props: {
    error: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
  },
  components: {
    DemographicsPieChartCard,
    DemographicsBarChartCard,
    ChartBlock,
    InsightsTableTitle,
    ReportMessage,
    TableTile,
  },
  data() {
    return {
      shopperIntelCard: insightsCardsMap.shopperIntelligence,
      statSigMessage: {
        title: 'Not Enough Data',
        description: [
          'The number of page views doesn’t meet MikMak’s minimum threshold.',
          'Please modify your report filters or date range to view a larger data set.',
        ],
        isAlert: true,
      },
    };
  },
  computed: {
    ...mapState({
      portfolioBrands: state => state.dashboard.portfolioBrands,
      demographicsOverviewData: state => state.dashboard.demographicsOverviewData,
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
    ...mapGetters([
      'demographicsEnabledPortfolioBrands',
    ]),
    isDemographicsEnabled() {
      return this.demographicsEnabledPortfolioBrands.length > 0;
    },
    selectedBrands() {
      return this.demographicsEnabledPortfolioBrands.filter(pb => pb.checked);
    },
    brandIds() {
      return this.selectedBrands.map(b => b.value);
    },
    encodedBrandIds() {
      return encodeURIComponent(JSON.stringify(this.brandIds));
    },
    hasBrandError() {
      return this.selectedBrands.length === 0;
    },
    showTooltip() {
      return this.isDemographicsEnabled &&
        this.demographicsEnabledPortfolioBrands.length !== this.portfolioBrands.length &&
        !this.hasBrandError;
    },
    tooltipMessage() {
      if (!this.showTooltip || this.selectedBrands.length === 0) return '';

      const base = 'The following selected brands have access to Shopper Intelligence:';

      let brands = ` ${this.selectedBrands[0].brandName}`;

      for (let i=1; i<this.selectedBrands.length; i++) {
        brands += `, ${this.selectedBrands[i].brandName}`;
      }

      return base + brands;
    },
    hasDemographicsError() {
      return !this.isDemographicsEnabled ||
        this.hasBrandError ||
        !this.demographicsOverviewData?.isStatisticallySignificant;
    },
    showReportMessage() {
      return !this.loading && this.hasDemographicsError;
    },
    formattedTableData() {
      if (!this.demographicsOverviewData?.segments?.top_segments) return [];

      return this.demographicsOverviewData.segments.top_segments.data.map(d => {
        return {
          full_segment_name: d.full_segment_name,
          measurable_views: d.page_views,
          purchase_intent_rate: d.purchase_intent_rate,
        };
      });
    },
    formattedBarChartData() {
      if (!this.demographicsOverviewData?.segments?.age_segments) return [];

      const ageDimension = getReportFieldFromValue(this.allReportFields, 'age');
      const ageDataCopy = [...this.demographicsOverviewData.segments.age_segments.data];

      const sortedData = ageDataCopy.sort((a, b) => {
        a = a[ageDimension.snake];
        b = b[ageDimension.snake];

        if (a === b) return 0;

        return a > b ? 1 : -1;
      });

      return sortedData.map(d => {
        const displayValue = d[ageDimension.snake].split(' years')[0];

        return {
          ...d,
          'Demographics_____Age': displayValue,
        };
      });
    },
    showBarChartThresholdMessage() {
      return !this.demographicsOverviewData?.segments?.age_segments?.isStatisticallySignificant;
    },
    formattedPieChartData() {
      if (!this.demographicsOverviewData?.segments?.gender_segments) return [];

      const colorMap = { 'Male': '#233786', 'Female': '#4369F4', 'Unknown': '#00A6A4' };
      const sortOrder = ['Female', 'Male', 'Unknown'];
      const dataCopy = [...this.demographicsOverviewData.segments.gender_segments.data];

      const genderDimension = getReportFieldFromValue(this.allReportFields, 'gender');

      const sortedData = dataCopy.sort((a, b) => {
        a = sortOrder.indexOf(a[genderDimension.snake]);
        b = sortOrder.indexOf(b[genderDimension.snake]);

        if (a === b) return 0;

        return a > b ? 1 : -1;
      });

      return sortedData.map(d => {
        const dimensionName = d[genderDimension.snake];
        const dimensionValue = this.formatPercent(d.pct_of_page_views);

        return {
          value: d.pct_of_page_views,
          color: colorMap[dimensionName],
          displayValue: dimensionValue,
          chartKey: dimensionName + ' ' + dimensionValue,
          metric: genderDimension,
          label: dimensionName,
        };
      });
    },
    showPieChartThresholdMessage() {
      return !this.demographicsOverviewData?.segments?.gender_segments?.isStatisticallySignificant;
    },
  },
  methods: {
    formatPercent(number) {
      const rounded = number.toFixed(0);

      return rounded + '%';
    },
    emitIconClick(title) {
      if (this.error) {
        return;
      }

      this.$emit('iconClick', title);
    },
  },
};
</script>

<style lang="scss">
.dashboard-row.message {
  .table-title {
    font-size: 24px;
  }
}
</style>

<style lang="scss" scoped>
.dashboard-row-demographics {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 16px 0px;

  ::v-deep .tile {
    margin: 0px;
    height: auto;
    height: 479px;
    flex-grow: 1;
  }
}

.dashboard-row {
  display: flex;
  flex-direction: row;
  height: auto;
  flex-wrap: wrap;
}

.big {
  margin-bottom: 37.5px;
  margin-right: -9px;
  margin-left: -9px;
}

.big .gender-chart {
  margin-bottom: 0px;
}

.insights-report-message {
  height: 400px;
  display: flex;
  flex-direction: column;
}

.shopper-intel-tile {
  margin-left: 0px;
  margin-right: 0px;
  margin-bottom: 0px;
}
</style>
